import { useEffect, useState } from 'react';
import { useAsyncFn } from 'react-use';

import {
  AddPromoCodeRequest,
  PromoCodeInfo,
  PromoCodeSubscription,
} from '../../../interfaces/promoCodeInfo';
import { PromoCodesService } from '../../../services/promoCodesService/promoCodesService';
import { updateItemByField } from '../../../../shared/utils/arrayUtils';

type UsePromoCodesReturn = {
  promoCodes: PromoCodeInfo[];
  subscriptions: PromoCodeSubscription[];
  loading: boolean;
  addPromoCode: (promoCode: AddPromoCodeRequest) => Promise<void>;
  togglePromoCode: (promoCodeId: number, pause: boolean) => Promise<void>;
};

export const usePromoCodes = (
  entityId: number,
  service: PromoCodesService,
  subscriptionTypeAvailable: boolean,
): UsePromoCodesReturn => {
  const [promoCodes, setPromoCodes] = useState<PromoCodeInfo[]>([]);
  const [subscriptions, setSubscriptions] = useState<PromoCodeSubscription[]>([]);

  const [fetchPromoCodesState, fetchPromoCodes] = useAsyncFn(async () => {
    const newPromoCodes = await service.getPromoCodes(entityId);

    setPromoCodes(newPromoCodes);
  });

  const [fetchSubscriptionsState, fetchSubscriptions] = useAsyncFn(async () => {
    const response = await service.getSubscriptions(entityId);

    setSubscriptions(response);
  });

  useEffect(() => {
    fetchPromoCodes();
    if (subscriptionTypeAvailable) {
      fetchSubscriptions();
    }
  }, []);

  const addPromoCode = async (promoCodeRequest: AddPromoCodeRequest) => {
    const response = await service.addPromoCode(entityId, promoCodeRequest);
    setPromoCodes((prevPromoCodes) => [...prevPromoCodes, response]);
  };

  const togglePromoCode = async (promoCodeId: number, pause: boolean) => {
    const response = await service.togglePromoCode(entityId, promoCodeId, pause);
    setPromoCodes((prev) => updateItemByField(prev, response, { id: response.id }));
  };

  return {
    promoCodes,
    subscriptions,
    loading: fetchSubscriptionsState.loading || fetchPromoCodesState.loading,
    addPromoCode,
    togglePromoCode,
  };
};
